import {
  getCrsAnalysisResult,
  getFutureHazardAnalysisResult,
  getCrsComponentAnalysisResult
} from "api/layer";
import { Button, CircularProgress } from "@mui/material";
import CrsLegend from "components/crsLegend/CrsLegend";
import Input from "components/input/input";
import Modal from "components/modal/modal";
import Table from "components/table/table";
import Tab from "containers/layers/tab/tab";
import domtoimage from "dom-to-image";
import { AppAction, AppContext } from "helpers/context";
import { getLanguage } from "helpers/translations";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./climate-risk-modal.module.scss";
import getDoc from "report/crs/document";
import {
  ClimateRiskReportCategory,
  ClimateRiskReportSection,
  ClimateRiskSspData
} from "../../../report/crs/types";
import generate from "report/generation";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";

enum ClimateCategory {
  CRS = "climate",
  FUTURE_HAZARD = "futureHazard",
  CRS_COMPONENT = "CRSComponent"
}

enum Tabs {
  SSP126 = "ssp126",
  SSP245 = "ssp245",
  SSP370 = "ssp370",
  SSP585 = "ssp585"
}

const ALL_REPORT_CATEGORIES: ClimateCategory[] = [
  ClimateCategory.CRS,
  ClimateCategory.FUTURE_HAZARD,
  ClimateCategory.CRS_COMPONENT
];

const EMPTY_CATEGORY_SSP_DATA: Partial<
  Record<ClimateCategory, ClimateRiskSspData>
> = {};

function ClimateRiskModal() {
  const [context, dispatch] = useContext(AppContext);
  const toggleButtonTextSx = {
    "& .MuiToggleButton-root": {
      color: "#0009"
    },
    "& .MuiToggleButton-root.Mui-selected": {
      color: "#2e7d32"
    },
    "& .MuiToggleButton-root.Mui-selected:hover": {
      color: "#2e7d32"
    }
  };

  const subTabsToggleSx = {
    ...toggleButtonTextSx,
    gap: "10px",
    "& .MuiToggleButtonGroup-grouped": {
      border: "none",
      borderRadius: "16px !important",
      padding: "0 10px",
      minWidth: "120px",
      height: "30px",
      lineHeight: "30px",
      backgroundColor: "#ededed"
    },
    "& .MuiToggleButtonGroup-grouped.Mui-selected": {
      backgroundColor: "#0e8a3f14"
    },
    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
      border: "none",
      borderRadius: "16px !important",
      marginLeft: 0
    }
  };

  const [tab, setTab] = useState<Tabs>(Tabs.SSP126);
  const [category, setCategory] = useState<ClimateCategory>(
    ClimateCategory.CRS
  );
  const activeCategoryRef = useRef<ClimateCategory>(ClimateCategory.CRS);
  const [sSP126Arr, setSSP126Arr] = useState<any[]>([]);
  const [sSP245Arr, setSSP245Arr] = useState<any[]>([]);
  const [sSP370Arr, setSSP370Arr] = useState<any[]>([]);
  const [sSP585Arr, setSSP585Arr] = useState<any[]>([]);
  const [graunch, setGraunch] = useState<boolean>(false);
  const [downloadModal, setDownloadModal] = useState(false);
  // const [isLoading, setIsLoading] = useState("0"); //0未开始 1下载中 2下载完成
  const [isLoading, setIsLoading] = useState(false); // true下载中 false下载完成
  const [downloadName, setDownloadName] = useState("");
  const pollTimersRef = useRef<
    Partial<Record<ClimateCategory, ReturnType<typeof setTimeout>>>
  >({});
  const requestCountRef = useRef<Record<ClimateCategory, number>>({
    [ClimateCategory.CRS]: 0,
    [ClimateCategory.FUTURE_HAZARD]: 0,
    [ClimateCategory.CRS_COMPONENT]: 0
  });
  type SspData = ClimateRiskSspData;
  const categorySspData =
    (context.climateRiskCategorySspData as Partial<
      Record<ClimateCategory, SspData>
    >) || EMPTY_CATEGORY_SSP_DATA;
  const categorySspDataRef =
    useRef<Partial<Record<ClimateCategory, SspData>>>(categorySspData);

  const [t] = useTranslation();

  const close = (): void => {
    dispatch({ type: AppAction.setClimateRiskModal, payload: false });
  };

  const clearPollTimer = (currentCategory: ClimateCategory) => {
    const timer = pollTimersRef.current[currentCategory];
    if (timer) {
      clearTimeout(timer);
      pollTimersRef.current[currentCategory] = undefined;
    }
  };

  const applySspData = (sspData: SspData) => {
    setSSP126Arr(sspData.SSP126Arr);
    setSSP245Arr(sspData.SSP245Arr);
    setSSP370Arr(sspData.SSP370Arr);
    setSSP585Arr(sspData.SSP585Arr);
    dispatch({
      type: AppAction.setSspData,
      payload: sspData
    });
  };

  const clearSspDataView = () => {
    setSSP126Arr((prev) => (prev.length === 0 ? prev : []));
    setSSP245Arr((prev) => (prev.length === 0 ? prev : []));
    setSSP370Arr((prev) => (prev.length === 0 ? prev : []));
    setSSP585Arr((prev) => (prev.length === 0 ? prev : []));
  };

  const setCategorySspData = (
    nextData: Partial<Record<ClimateCategory, SspData>>
  ) => {
    categorySspDataRef.current = nextData;
    dispatch({
      type: AppAction.setClimateRiskCategorySspData,
      payload: nextData
    });
  };

  const currentCategoryData = categorySspData[category];
  const tableData = useMemo(() => {
    if (!currentCategoryData) {
      return [];
    }
    if (tab === Tabs.SSP126) {
      return currentCategoryData.SSP126Arr;
    }
    if (tab === Tabs.SSP245) {
      return currentCategoryData.SSP245Arr;
    }
    if (tab === Tabs.SSP370) {
      return currentCategoryData.SSP370Arr;
    }
    return currentCategoryData.SSP585Arr;
  }, [currentCategoryData, tab]);

  const selectedLayers = context.singleAnalysisSelectedLayers || [];
  const hasClimateCategory = !!selectedLayers[1]?.length;
  const hasFutureHazardCategory = !!selectedLayers[2]?.length;
  const hasCrsComponentCategory = !!selectedLayers[3]?.length;
  const visibleCategories = [
    hasClimateCategory ? ClimateCategory.CRS : undefined,
    hasFutureHazardCategory ? ClimateCategory.FUTURE_HAZARD : undefined,
    hasCrsComponentCategory ? ClimateCategory.CRS_COMPONENT : undefined
  ].filter(Boolean) as ClimateCategory[];

  const reportSections = useMemo<ClimateRiskReportSection[]>(() => {
    if (visibleCategories.length === 0) {
      return [];
    }

    const sections: ClimateRiskReportSection[] = [];
    const emptySSPData: ClimateRiskSspData = {
      SSP126Arr: [],
      SSP245Arr: [],
      SSP370Arr: [],
      SSP585Arr: []
    };
    const categoryTitleKey: Record<ClimateCategory, string> = {
      [ClimateCategory.CRS]: "options.climate",
      [ClimateCategory.FUTURE_HAZARD]: "options.futureHazard",
      [ClimateCategory.CRS_COMPONENT]: "options.CRSComponent"
    };
    const categoryEmptyReasonKey: Record<ClimateCategory, string> = {
      [ClimateCategory.CRS]: "report.crsSectionEmptyDesc",
      [ClimateCategory.FUTURE_HAZARD]: "report.futureClimateRiskEmptyDesc",
      [ClimateCategory.CRS_COMPONENT]: "report.climateVariablesEmptyDesc"
    };

    ALL_REPORT_CATEGORIES.forEach((currentCategory) => {
      const isSelectedCategory = visibleCategories.includes(currentCategory);
      const sspData = categorySspData[currentCategory];

      if (!isSelectedCategory || !sspData) {
        sections.push({
          category: currentCategory as ClimateRiskReportCategory,
          title: t(categoryTitleKey[currentCategory]),
          sspData: emptySSPData,
          isEmpty: true,
          emptyReason: categoryEmptyReasonKey[currentCategory]
        });
        return;
      }

      sections.push({
        category: currentCategory as ClimateRiskReportCategory,
        title: t(categoryTitleKey[currentCategory]),
        sspData
      });
    });

    return sections;
  }, [categorySspData, t, visibleCategories]);
  const canDownloadReport = visibleCategories.length > 0;

  const getBatchIdByCategory = (
    currentCategory: ClimateCategory
  ): string | undefined => {
    if (currentCategory === ClimateCategory.FUTURE_HAZARD) {
      return context.futureHazardBatchId;
    }
    if (currentCategory === ClimateCategory.CRS_COMPONENT) {
      return context.crsComponentBatchId;
    }
    return context.batchId;
  };

  const getLookupByCategory = (currentCategory: ClimateCategory) => {
    if (currentCategory === ClimateCategory.FUTURE_HAZARD) {
      return getFutureHazardAnalysisResult;
    }
    if (currentCategory === ClimateCategory.CRS_COMPONENT) {
      return getCrsComponentAnalysisResult;
    }
    return getCrsAnalysisResult;
  };

  const getScoreItems = (res: any): any[] => {
    const result = res?.data?.results?.[0];
    return (
      result?.climateRiskScores ||
      result?.futureHazardRiskScores ||
      result?.crsComponentRiskScores ||
      []
    );
  };

  const getClimateRiskData = async (
    currentCategory: ClimateCategory,
    force = false
  ) => {
    if (!context.climateRiskModal) {
      requestCountRef.current[currentCategory] = 0;
      clearPollTimer(currentCategory);
      return;
    }

    if (!force && categorySspData[currentCategory]) {
      if (activeCategoryRef.current === currentCategory) {
        applySspData(categorySspData[currentCategory] as SspData);
      }
      return;
    }

    const batchId = getBatchIdByCategory(currentCategory);
    if (!batchId || batchId === "running" || batchId === "error") {
      if (activeCategoryRef.current === currentCategory) {
        setGraunch(true);
      }
      return;
    }

    getLookupByCategory(currentCategory)(batchId)
      .then((res) => {
        requestCountRef.current[currentCategory] += 1;
        if (requestCountRef.current[currentCategory] >= 10) {
          if (activeCategoryRef.current === currentCategory) {
            setGraunch(true);
          }
          clearPollTimer(currentCategory);
          return;
        }
        if (res.data.status !== "Completed") {
          clearPollTimer(currentCategory);
          pollTimersRef.current[currentCategory] = setTimeout(() => {
            getClimateRiskData(currentCategory, true);
          }, 3000);
          return;
        }
        requestCountRef.current[currentCategory] = 0;
        clearPollTimer(currentCategory);

        let SSP126Arr: any = [],
          SSP245Arr: any = [],
          SSP370Arr: any = [],
          SSP585Arr: any = [];
        const scoreItems = getScoreItems(res);
        scoreItems.forEach((item: any) => {
          item.scenarios.forEach((scenario: any) => {
            let arr: any = [],
              isSeaLevelRise = 0; // 用于判断是否为sea_level_rise SSP3-7.0 不为0
            arr.push(item.index);
            scenario.bandGroups[0].bands.forEach((band: any) => {
              if (band.value === "NaN") {
                arr.push("");
              } else {
                if (
                  item.index === "sea_level_rise" &&
                  scenario.name === "SSP3-7.0" &&
                  band.value * 1 !== 0
                ) {
                  isSeaLevelRise++;
                }
                arr.push((band.value * 1).toFixed(2));
              }
            });
            if (scenario.name === "SSP1-2.6") {
              SSP126Arr.push(arr);
            } else if (scenario.name === "SSP2-4.5") {
              SSP245Arr.push(arr);
            } else if (scenario.name === "SSP3-7.0") {
              if (arr[0] === "sea_level_rise" && isSeaLevelRise === 0) {
                // 如果是sea_level_rise且为全为0，则置灰
                SSP370Arr.push([
                  "sea_level_rise",
                  "NaN",
                  "NaN",
                  "NaN",
                  "NaN",
                  "NaN",
                  "NaN",
                  "NaN",
                  "NaN",
                  "NaN"
                ]);
              } else {
                SSP370Arr.push(arr);
              }
            } else if (scenario.name === "SSP5-8.5") {
              SSP585Arr.push(arr);
            }
          });
        });
        if (SSP126Arr.length != SSP370Arr.length) {
          SSP370Arr.unshift([
            "drought_old",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN"
          ]);
        }
        const sspData: SspData = { SSP126Arr, SSP245Arr, SSP370Arr, SSP585Arr };
        const nextCategorySspData = {
          ...categorySspDataRef.current,
          [currentCategory]: sspData
        };
        setCategorySspData(nextCategorySspData);
        if (activeCategoryRef.current === currentCategory) {
          applySspData(sspData);
          setGraunch(false);
        }
      })
      .catch((error) => {
        clearPollTimer(currentCategory);
        if (activeCategoryRef.current === currentCategory) {
          setGraunch(true);
        }
      });
  };

  useEffect(() => {
    if (context.climateRiskModal) {
      setGraunch(false);
      setTab(Tabs.SSP126);
      const defaultCategory = visibleCategories[0] || ClimateCategory.CRS;
      setCategory(defaultCategory);

      if (categorySspData[defaultCategory]) {
        applySspData(categorySspData[defaultCategory] as SspData);
      }

      visibleCategories.forEach((currentCategory) => {
        if (!categorySspData[currentCategory]) {
          getClimateRiskData(currentCategory, true);
        }
      });
    }
  }, [context.climateRiskModal]);

  useEffect(() => {
    if (!context.climateRiskModal) {
      Object.values(ClimateCategory).forEach((c) => clearPollTimer(c));
    }
  }, [context.climateRiskModal]);

  useEffect(() => {
    categorySspDataRef.current = categorySspData;
  }, [categorySspData]);

  useEffect(() => {
    activeCategoryRef.current = category;
  }, [category]);

  useEffect(() => {
    if (categorySspData[category]) {
      applySspData(categorySspData[category] as SspData);
      setGraunch(false);
    } else {
      clearSspDataView();
    }
  }, [category, categorySspData]);

  const downloadPdf = async () => {
    if (isLoading) {
      return;
    }
    //这里下载的PDF,需要包含这个页面上三个TAB页的内容，比如气候风险评分/未来气候风险/气候风险变量，样式参考《目标CRS新需求.pdf》，气候风险变量的表格没有颜色
    let lng =
      !context.currentMultipleLocationInfo && context.searchLocationInfo
        ? context.searchLocationInfo?.[context.language]?.location?.split(
            ","
          )[0]
        : context.currentMultipleLocationInfo.longitude;
    let lat =
      !context.currentMultipleLocationInfo && context.searchLocationInfo
        ? context.searchLocationInfo?.[context.language]?.location?.split(
            ","
          )[1]
        : context.currentMultipleLocationInfo.latitude;

    let province;
    if (!context.currentMultipleLocationInfo) {
      province =
        getLanguage() === "en"
          ? context.searchLocationInfo.en.province
          : context.searchLocationInfo.cn.province;
    } else {
      province = context.currentMultipleLocationInfo.province;
    }

    let county;
    if (!context.currentMultipleLocationInfo) {
      county =
        getLanguage() === "en"
          ? context.searchLocationInfo.en.district
          : context.searchLocationInfo.cn.district;
    } else {
      county = context.currentMultipleLocationInfo.place;
    }

    province = province || "";
    county = county || "";

    setIsLoading(true);
    setDownloadModal(false);
    try {
      const dd = await getDoc({
        t,
        name: downloadName,
        lng,
        lat,
        province,
        county,
        sections: reportSections
      });

      await generate(dd, downloadName);
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "reportGenerated",
          message: "reportGeneratedSuccess"
        }
      });
    } catch {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "downloadPdfTitle",
          titleIconColor: "yellow",
          titleIcon: "warning",
          resMessage: t("climateRiskModal.downloadError")
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      header={t("climateRiskModal.title")}
      opened={context.climateRiskModal}
      onClose={close}
    >
      <div className={styles.risk}>
        {visibleCategories.length > 0 && (
          <div className={styles.tabs}>
            <ToggleButtonGroup
              exclusive
              value={category}
              size="small"
              sx={toggleButtonTextSx}
              onChange={(_, newValue) => {
                if (!newValue) return;
                setGraunch(false);
                setCategory(newValue);
                setTab(Tabs.SSP126);
              }}
            >
              {hasClimateCategory && (
                <ToggleButton value={ClimateCategory.CRS}>
                  {t("options.single.climateRiskScores")}
                </ToggleButton>
              )}
              {hasFutureHazardCategory && (
                <ToggleButton value={ClimateCategory.FUTURE_HAZARD}>
                  {t("options.single.futureHazards")}
                </ToggleButton>
              )}
              {hasCrsComponentCategory && (
                <ToggleButton value={ClimateCategory.CRS_COMPONENT}>
                  {t("options.single.climateVariables")}
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </div>
        )}
        <div className={styles.subTabs}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={tab}
            sx={subTabsToggleSx}
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setTab(newValue);
              }
            }}
          >
            <ToggleButton value={Tabs.SSP126}>SSP 1-2.6</ToggleButton>
            <ToggleButton value={Tabs.SSP245}>SSP 2-4.5</ToggleButton>
            <ToggleButton value={Tabs.SSP370}>SSP 3-7.0</ToggleButton>
            <ToggleButton value={Tabs.SSP585}>SSP 5-8.5</ToggleButton>
          </ToggleButtonGroup>
        </div>
        {category !== ClimateCategory.CRS_COMPONENT && (
          <>
            <div className={styles.description}>
              {category === ClimateCategory.FUTURE_HAZARD
                ? t("climateRiskModal.futureHazardsDescription")
                : t("climateRiskModal.description")}
            </div>
            <div className={styles.legend}>
              <CrsLegend category={category === ClimateCategory.FUTURE_HAZARD ? "futureHazard" : "crs"} />
            </div>
          </>
        )}
        <div className={styles.title}>{t(`climateRiskModal.${tab}.title`)}</div>
        <div className={styles.info}>{t(`climateRiskModal.${tab}.info`)}</div>
        <Table
          tableData={tableData}
          type={category}
          graunch={graunch}
          handleRetry={() => {
            setGraunch(false);
            getClimateRiskData(category);
          }}
        />

        <div className={styles.button}>
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={isLoading || !canDownloadReport}
            endIcon={
              isLoading && <CircularProgress color="success" size={24} />
            }
            onClick={() => {
              setDownloadModal(true);
              setDownloadName("");
            }}
          >
            {t("buttons.download")}
          </Button>
          &nbsp;&nbsp;
          <Button color="success" variant="contained" onClick={close}>
            {t("buttons.close")}
          </Button>
        </div>
      </div>
      <Modal
        header={t("single.downloadTitle")}
        opened={downloadModal}
        onClose={() => setDownloadModal(false)}
      >
        <div className={styles.analyse}>
          <div className="mt-5 mb-5">
            <Input
              value={downloadName}
              isClose={true}
              placeholder={t("single.downloadPlaceholder")}
              onChange={(event) => setDownloadName(event.target.value)}
              onClose={() => setDownloadName("")}
            />
          </div>
          <div className="flex justify-between mt-5">
            <div>
              <Button color="success" onClick={() => setDownloadName("")}>
                {t("buttons.reset")}
              </Button>
            </div>
            <div>
              <Button color="success" onClick={() => setDownloadModal(false)}>
                {t("buttons.close")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={isLoading || !downloadName || !canDownloadReport}
                endIcon={
                  isLoading && <CircularProgress color="success" size={20} />
                }
                onClick={downloadPdf}
              >
                {t("buttons.download")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}

export default ClimateRiskModal;
